import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { useGetUserSettingsQuery } from '~/redux/apis/user-settings-api'
import { CurriculumTutorialStatusEnum, CurriculumTutorialUserStatus } from '~/redux/constants/tags.const'
import { setShouldShowTutorial } from '~/redux/slices/dashboard'
import { TutorialProducts } from '~/dashboard/types/dashboard.types'

interface UseCurriculumTutorialStatusParams {
  userId?: number
  isQuizComplete: boolean
  hasPrerequisite: boolean
  isLoading: boolean
  mappingQuizApplicable: boolean
}

export const useHandleCurriculumTutorial = ({
  userId,
  isQuizComplete,
  hasPrerequisite,
  isLoading,
  mappingQuizApplicable,
}: UseCurriculumTutorialStatusParams): CurriculumTutorialStatusEnum => {
  const dispatch = useDispatch()

  const { data: userSettings, isFetching } = useGetUserSettingsQuery(undefined, {
    skip: !userId || !mappingQuizApplicable,
  })

  const curriculumTutorialStatus =
    userSettings?.curriculumTutorialStatus || CurriculumTutorialUserStatus.CompletedTutorial2

  const tutorialStatus = useMemo(() => {
    if (curriculumTutorialStatus === CurriculumTutorialUserStatus.CompletedTutorial2 || isFetching || isLoading) {
      return CurriculumTutorialStatusEnum.DO_NOT_SHOW_TUTORIAL
    }

    if (curriculumTutorialStatus === CurriculumTutorialUserStatus.NotStarted && !isQuizComplete) {
      return CurriculumTutorialStatusEnum.SHOW_TUTORIAL1
    }

    if (isQuizComplete && !hasPrerequisite && curriculumTutorialStatus === CurriculumTutorialUserStatus.NotStarted) {
      return CurriculumTutorialStatusEnum.SHOW_TUTORIAL1
    }

    if (
      isQuizComplete &&
      hasPrerequisite &&
      (curriculumTutorialStatus === CurriculumTutorialUserStatus.NotStarted ||
        curriculumTutorialStatus === CurriculumTutorialUserStatus.CompletedTutorial1)
    ) {
      return CurriculumTutorialStatusEnum.SHOW_TUTORIAL2
    }

    return CurriculumTutorialStatusEnum.DO_NOT_SHOW_TUTORIAL
  }, [curriculumTutorialStatus, isFetching, isLoading, isQuizComplete, hasPrerequisite])

  useEffect(() => {
    if (!mappingQuizApplicable || !userId) {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
      return
    }

    if (tutorialStatus === CurriculumTutorialStatusEnum.SHOW_TUTORIAL1) {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.CURRICULUM_TUTORIAL_1, shouldShowTutorial: true }))
    } else if (tutorialStatus === CurriculumTutorialStatusEnum.SHOW_TUTORIAL2 && hasPrerequisite) {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.CURRICULUM_TUTORIAL_2, shouldShowTutorial: true }))
    } else {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
    }
  }, [dispatch, mappingQuizApplicable, tutorialStatus, hasPrerequisite, userId])

  return tutorialStatus
}
